import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { getNicheMeta } from "../components/niche/nicheMeta";
import {
  getProfileThemeStyle,
  resolveProfileThemeId,
} from "../constants/profileThemeCatalog";
import { checkFollowing, followingIdsFromUser, isFollowingListPrivate, toggleFollow } from "../services/followService";
import FollowingOverlay, { FollowingStatPill } from "../components/profile/FollowingOverlay";
import {
  LOGGED_OUT_SIGNIN_PATH,
  MaybeLoggedOutLockedSection,
  loggedOutSignInProps,
} from "../components/profile/LoggedOutProfileGate";
import { getCreatorTiers } from "../services/supportTierService";
import { fetchUserByHandle } from "../services/userLookupService";
import { hasCreatorAccess } from "../utils/roleUtils";
import AvatarWithTrophy from "../components/profile/AvatarWithTrophy";
import CreatorDropCard, { DropFilterPills, filterDrops } from "../components/profile/CreatorDropCard";
import AppreciationWallTab, { countPendingWallNotes } from "../components/profile/AppreciationWallTab";
import TrophyCaseTab from "../components/profile/TrophyCaseTab";
import TipJarSheet, { TipJarProfileRow } from "../components/tips/TipJarSheet";
import NominateCreatorButton from "../components/credits/NominateCreatorButton";
import WallNoteModal, { WallNoteTriggerButton } from "../components/credits/WallNoteModal";
import CreatorTierAccordion from "../components/Subscription/CreatorTierAccordion";
import SubscribeModal from "../components/Subscription/SubscribeModal";
import { formatCount } from "../utils/formatCount";
import { setPageTitle } from "../components/DocumentTitle";
import PipAnchor from "../components/Pip/PipAnchor";
import PipPageTip from "../components/Pip/PipPageTip";

import "./CreatorProfile.css";
import "../Styles/profileThemes.css";

const STROKE = 1.8;

export default function CreatorProfile() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshUserProfile } = useAuth();

  const [creator, setCreator] = useState(null);
  const [drops, setDrops] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("drops");
  const [dropFilter, setDropFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [pendingWallCount, setPendingWallCount] = useState(0);
  const [monthlyEarnings, setMonthlyEarnings] = useState(0);
  const [tipOpen, setTipOpen] = useState(false);
  const [wallNoteOpen, setWallNoteOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeTierIndex, setSubscribeTierIndex] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [followingOpen, setFollowingOpen] = useState(false);

  const isOwner = !!(currentUser && creator && currentUser.uid === creator.uid);
  const isLoggedOut = !currentUser;
  const nicheMeta = getNicheMeta(creator?.niche);
  const profileThemeId = resolveProfileThemeId(creator?.profileTheme);
  const profileThemeStyle = getProfileThemeStyle(profileThemeId);
  const creatorHandle = creator?.handle || creator?.username || handle;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const data = await fetchUserByHandle(handle);
        if (cancelled) return;
        if (!data) {
          console.log("[CreatorProfile lookup] No user found — showing Creator not found");
          setCreator(null);
          setLoading(false);
          return;
        }
        if (!hasCreatorAccess(data) && data.role !== "creator") {
          navigate(`/fan/${data.uid}`, { replace: true });
          return;
        }
        setCreator(data);
        setPageTitle(`100ViewsOnly — ${data.displayName || data.username || handle || "Creator"}`);

        // Always fetch real drops (SEO + logged-out blur preview). Isolate
        // auth-only extras so a permission error cannot wipe the profile.
        const dropsSnap = await getDocs(query(collection(db, "drops"), where("creatorId", "==", data.uid)));
        if (cancelled) return;
        setDrops(
          dropsSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
        );

        const [tierList, earningsSnap] = await Promise.all([
          getCreatorTiers(data.uid).catch(() => []),
          getDoc(doc(db, "earnings", data.uid)).catch(() => null),
        ]);
        if (cancelled) return;
        setTiers(tierList || []);
        setMonthlyEarnings(earningsSnap?.exists() ? Number(earningsSnap.data()?.monthToDate || 0) : 0);
      } catch (err) {
        console.error("Creator profile load error:", err);
        if (!cancelled) {
          setCreator(null);
          setLoadError(
            err?.code === "permission-denied" || /sign in/i.test(err?.message || "")
              ? "Sign in to view creator profiles."
              : (err?.message || "Could not load this creator.")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [handle, navigate]);

  useEffect(() => {
    if (!currentUser || !creator || isOwner) return;
    checkFollowing({ followerId: currentUser.uid, creatorId: creator.uid }).then(setFollowing);
  }, [currentUser, creator, isOwner]);

  useEffect(() => {
    if (!creator?.uid || !isOwner) return;
    countPendingWallNotes(creator.uid).then(setPendingWallCount);
  }, [creator?.uid, isOwner]);

  const filteredDrops = useMemo(() => filterDrops(drops, dropFilter), [drops, dropFilter]);
  const openCount = drops.filter((d) => (Number(d.viewCount ?? 0)) < (d.maxViews || 100)).length;
  const featuredDrop = filteredDrops[0];
  const gridDrops = filteredDrops.slice(featuredDrop ? 1 : 0);
  const creatorFirst = creator?.displayName?.split(" ")[0] || "this creator";
  const signInHotspot = loggedOutSignInProps(isLoggedOut, navigate);

  const openSubscribeModal = (tierIndex = null) => {
    if (!currentUser) {
      navigate(`/welcome?auth=signin&redirect=${encodeURIComponent(`/creator/${creatorHandle}`)}`);
      return;
    }
    setSubscribeTierIndex(tierIndex);
    setSubscribeOpen(true);
  };

  const handleFollow = async () => {
    if (!currentUser) {
      navigate(LOGGED_OUT_SIGNIN_PATH);
      return;
    }
    if (!creator || followLoading) return;
    setFollowLoading(true);
    try {
      const next = await toggleFollow({
        followerId: currentUser.uid,
        creatorId: creator.uid,
        isFollowing: following,
        creatorUsername: creatorHandle,
        creatorDisplayName: creator.displayName,
        creatorAvatar: creator.avatar || creator.avatarUrl,
      });
      setFollowing(next);
    } catch (err) {
      console.error("Follow toggle error:", err);
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return <div className="cp-page cp-page--loading">Loading profile…</div>;
  }

  if (!creator) {
    return (
      <div className="cp-page cp-page--empty">
        <h1>{loadError ? "Can't open profile" : "Creator not found"}</h1>
        <p>
          {loadError
            || `@${handle} doesn't exist or hasn't joined yet.`}
        </p>
        {loadError?.toLowerCase().includes("sign in") ? (
          <Link to="/welcome?auth=signin" className="cp-btn cp-btn--gold">Sign in</Link>
        ) : (
          <Link to="/gallery" className="cp-btn cp-btn--gold">Browse creators</Link>
        )}
      </div>
    );
  }

  const tags = [
    nicheMeta.label,
    ...(creator.tags || []),
    creator.location,
  ].filter(Boolean);

  return (
    <div className="cp-page" data-profile-theme={profileThemeId} style={profileThemeStyle}>
      {isOwner && (
        <div className="cp-owner-bar">
          <div className="cp-ob-left">
            <span className="cp-ob-dot" />
            <span className="cp-ob-text">You&apos;re viewing your public profile</span>
            <span className="cp-ob-hand">— how fans see you</span>
          </div>
          <div className="cp-ob-right">
            <span className="cp-ob-earn">${monthlyEarnings.toFixed(2)}</span>
            <span className="cp-ob-earn-lbl">this month</span>
            <Link to="/themes" className="cp-ob-btn cp-obb-niche">Themes</Link>
            <Link to={`/creator/${creatorHandle}/edit`} className="cp-ob-btn cp-obb-ghost">Edit Profile</Link>
            <Link to="/broadcasts" className="cp-ob-btn cp-obb-ghost">Send broadcast</Link>
            <Link to="/post" className="cp-ob-btn cp-obb-gold">New Drop</Link>
          </div>
        </div>
      )}

      <header className={`cp-header${isOwner ? " cp-header--owner" : ""}`}>
        <div
          className={`cp-banner${isLoggedOut ? " cp-signin-hotspot" : ""}`}
          {...(isLoggedOut ? signInHotspot : {})}
        >
          {(creator.bannerImage || creator.bannerUrl) && (
            <div
              className="cp-banner-bg"
              style={{ backgroundImage: `url(${creator.bannerImage || creator.bannerUrl})` }}
            />
          )}
          <div className="cp-banner-texture" />
          <div className="cp-banner-vignette" />
          <div className="cp-banner-accent" />
          <div className="cp-niche-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--niche)" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s-8-4.5-8-11.8A8 8 0 0112 2a8 8 0 018 8.2c0 7.3-8 11.8-8 11.8z" />
            </svg>
            <span className="cp-nb-text">{nicheMeta.label}</span>
            {creator.location && <span className="cp-nb-hand">— {creator.location}</span>}
          </div>
        </div>

        <div className="cp-info">
          <div
            className={`cp-av-wrap${isLoggedOut ? " cp-signin-hotspot" : ""}`}
            {...(isLoggedOut ? signInHotspot : {})}
          >
            <AvatarWithTrophy
              src={creator.avatar || creator.avatarUrl}
              alt={creator.displayName || creatorHandle}
              imgClassName="cp-av"
              size="lg"
              pinnedTrophy={creator.pinnedTrophy}
              ring
              verified
            />
          </div>

          <div className="cp-meta">
            <h1 className="cp-name">{creator.displayName || creatorHandle}</h1>
            <div className="cp-handle-row">
              <span>@{creatorHandle}</span>
              <span className="cp-handle-tag">{nicheMeta.label} Creator</span>
              {creator.foundingCreator && <span className="cp-handle-tag">Founding Creator</span>}
            </div>
            {creator.bio && <p className="cp-bio">{creator.bio}</p>}
            <div className="cp-tags">
              {tags.map((tag) => (
                <span key={tag} className={`cp-tag${tag === nicheMeta.label ? " cp-tag--niche" : ""}`}>{tag}</span>
              ))}
            </div>
          </div>

          <div className="cp-actions">
            <div className="cp-stats-row">
              <div className="cp-stat"><div className="cp-stat-num">{formatCount(creator.followerCount || creator.subscriberCount || 0)}</div><div className="cp-stat-lbl">Subscribers</div></div>
              <div className="cp-stat"><div className="cp-stat-num">{drops.length}</div><div className="cp-stat-lbl">Drops</div></div>
              <FollowingStatPill
                count={followingIdsFromUser(creator).length}
                isPrivate={isFollowingListPrivate(creator)}
                onClick={() => (isLoggedOut ? navigate(LOGGED_OUT_SIGNIN_PATH) : setFollowingOpen(true))}
              />
              {creator.social?.instagram && <div className="cp-stat"><div className="cp-stat-num">{formatCount(creator.social.instagramFollowers || 0)}</div><div className="cp-stat-lbl">Instagram</div></div>}
              {creator.social?.tiktok && <div className="cp-stat"><div className="cp-stat-num">{formatCount(creator.social.tiktokFollowers || 0)}</div><div className="cp-stat-lbl">TikTok</div></div>}
            </div>
            {isOwner ? (
              <div className="cp-owner-actions">
                <Link to={`/creator/${creatorHandle}/edit`} className="cp-oa-btn cp-oab-primary">Edit Profile</Link>
                <Link to="/broadcasts" className="cp-oa-btn cp-oab-ghost">Send broadcast</Link>
                <Link to="/dashboard" className="cp-oa-btn cp-oab-ghost">Dashboard</Link>
                <Link to="/earnings" className="cp-oa-btn cp-oab-dark">Earnings</Link>
              </div>
            ) : (
              <div className="cp-action-btns">
                <PipAnchor id="onboard-follow" style={{ display: "inline-flex" }}>
                  <button
                    type="button"
                    className={`cp-btn-follow${following ? " cp-btn-follow--active" : ""}`}
                    onClick={handleFollow}
                    disabled={followLoading}
                  >
                    {following ? "Following ✓" : "Follow"}
                  </button>
                </PipAnchor>
                {currentUser && (
                  <>
                    <PipPageTip
                      anchorId="onboard-follow"
                      flag="seenFollowTip"
                      text="Tap Follow to see their new drops in your feed."
                    />
                    <NominateCreatorButton
                      creatorId={creator.uid}
                      creatorName={creator.displayName || creatorHandle}
                      variant="compact"
                      className="cp-nominate-inline"
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <MaybeLoggedOutLockedSection locked={isLoggedOut} name={creatorFirst} noun="drops">
          <div className={`cp-tabs-wrap${isOwner ? " cp-tabs-wrap--owner" : ""}`}>
        <div className="cp-tabs">
          <button type="button" className={`cp-tab${activeTab === "drops" ? " cp-tab--active" : ""}`} onClick={() => setActiveTab("drops")}>Drops</button>
          <button type="button" className={`cp-tab${activeTab === "about" ? " cp-tab--active" : ""}`} onClick={() => setActiveTab("about")}>About</button>
          <button type="button" className={`cp-tab${activeTab === "wall" ? " cp-tab--active" : ""}`} onClick={() => setActiveTab("wall")}>
            Appreciation Wall
            {isOwner && pendingWallCount > 0 && <span className="cp-tab-badge">{pendingWallCount}</span>}
          </button>
          {isOwner && (
            <button type="button" className={`cp-tab${activeTab === "trophies" ? " cp-tab--active" : ""}`} onClick={() => setActiveTab("trophies")}>
              Trophy Case
            </button>
          )}
          {isOwner && (
            <Link to="/post" className="cp-tab-new">+ New Drop</Link>
          )}
        </div>
      </div>

      <div className="cp-body">
        <main className="cp-main">
          {activeTab === "drops" && (
            <section className="cp-drops-section">
              <div className="cp-drops-header">
                <div>
                  <h2 className="cp-dh-title">All <em>drops</em></h2>
                  <span className="cp-dh-hand">{drops.length} total · {openCount} open now</span>
                </div>
                <DropFilterPills value={dropFilter} onChange={setDropFilter} />
              </div>
              {filteredDrops.length === 0 ? (
                <div className="cp-drops-empty">
                  <p>{isOwner ? "Post your first drop to get started." : "No drops yet."}</p>
                  {isOwner && <Link to="/post" className="cp-btn cp-btn--gold">Create drop</Link>}
                </div>
              ) : (
                <div className="cp-drops-grid">
                  {featuredDrop && (
                    <CreatorDropCard drop={featuredDrop} featured isOwner={isOwner} nicheStyle={profileThemeStyle} />
                  )}
                  {gridDrops.map((drop) => (
                    <CreatorDropCard key={drop.id} drop={drop} isOwner={isOwner} nicheStyle={profileThemeStyle} />
                  ))}
                </div>
              )}
            </section>
          )}

          {activeTab === "about" && (
            <section className="cp-about-section">
              <h2 className="cp-dh-title">About <em>{creator.displayName?.split(" ")[0] || "creator"}</em></h2>
              <p className="cp-about-bio">{creator.bio || "No bio yet."}</p>
              {creator.location && <p className="cp-about-loc"><strong>Location:</strong> {creator.location}</p>}
            </section>
          )}

          {activeTab === "wall" && (
            <AppreciationWallTab creatorId={creator.uid} isOwner={isOwner} pendingCount={pendingWallCount} />
          )}

          {activeTab === "trophies" && isOwner && (
            <TrophyCaseTab
              profileUser={creator}
              isOwnProfile
              avatarUrl={creator.avatar || creator.avatarUrl}
            />
          )}
        </main>

        <aside className="cp-sidebar">
          {!isOwner && tiers.length > 0 && (
            <div className="cp-sidebar-stack">
              <CreatorTierAccordion
                tiers={tiers}
                creatorFirstName={creatorFirst}
                onSubscribe={(_tier, index) => openSubscribeModal(index)}
              />
              <div className="cp-sidebar-extras">
                <TipJarProfileRow creator={creator} onOpen={() => setTipOpen(true)} />
                <WallNoteTriggerButton creator={creator} onOpen={() => setWallNoteOpen(true)} />
              </div>
            </div>
          )}

          {!isOwner && tiers.length === 0 && (
            <div className="cp-sidebar-extras cp-sidebar-extras--solo">
              <TipJarProfileRow creator={creator} onOpen={() => setTipOpen(true)} />
              <WallNoteTriggerButton creator={creator} onOpen={() => setWallNoteOpen(true)} />
            </div>
          )}

          {isOwner && (
            <div className="cp-earnings-card">
              <div className="cp-ec-label">This month</div>
              <div className="cp-ec-amount">${monthlyEarnings.toFixed(2)}</div>
              <div className="cp-ec-sub">CAD · before fees</div>
              <Link to="/earnings" className="cp-ec-link">View earnings →</Link>
            </div>
          )}

          <div className="cp-about-card">
            <div className="cp-about-label">Quick stats</div>
            <div className="cp-about-stat">
              <div className="cp-about-stat-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--niche)" strokeWidth={STROKE} strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
              </div>
              <div><div className="cp-about-stat-val">{formatCount(creator.followerCount || 0)}</div><div className="cp-about-stat-lbl">Subscribers</div></div>
            </div>
            <div className="cp-about-stat">
              <div className="cp-about-stat-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--niche)" strokeWidth={STROKE} strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /></svg>
              </div>
              <div><div className="cp-about-stat-val">{drops.length}</div><div className="cp-about-stat-lbl">Total drops</div></div>
            </div>
          </div>

          {(creator.social?.instagram || creator.social?.tiktok || creator.social?.youtube) && (
            <div className="cp-social-card">
              <div className="cp-social-label">Elsewhere</div>
              <div className="cp-social-links">
                {creator.social?.instagram && (
                  <a href={`https://instagram.com/${creator.social.instagram.replace("@", "")}`} className="cp-social-link" target="_blank" rel="noreferrer">
                    <span className="cp-sl-icon cp-sl-icon--ig">IG</span>
                    <span><div className="cp-sl-name">Instagram</div><div className="cp-sl-handle">{creator.social.instagram}</div></span>
                  </a>
                )}
                {creator.social?.tiktok && (
                  <a href={`https://tiktok.com/@${creator.social.tiktok.replace("@", "")}`} className="cp-social-link" target="_blank" rel="noreferrer">
                    <span className="cp-sl-icon cp-sl-icon--tt">TT</span>
                    <span><div className="cp-sl-name">TikTok</div><div className="cp-sl-handle">{creator.social.tiktok}</div></span>
                  </a>
                )}
                {creator.social?.youtube && (
                  <a href={creator.social.youtube.startsWith("http") ? creator.social.youtube : `https://youtube.com/@${creator.social.youtube}`} className="cp-social-link" target="_blank" rel="noreferrer">
                    <span className="cp-sl-icon cp-sl-icon--yt">YT</span>
                    <span><div className="cp-sl-name">YouTube</div><div className="cp-sl-handle">{creator.social.youtube}</div></span>
                  </a>
                )}
              </div>
            </div>
          )}

          {creator.foundingCreator && (
            <div className="cp-founding-card">
              <div className="cp-founding-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={STROKE} strokeLinecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
              </div>
              <div>
                <div className="cp-founding-title">Founding Creator</div>
                <div className="cp-founding-sub">Locked 10% platform fee — forever.</div>
                <span className="cp-founding-hand">one of the originals 🍁</span>
              </div>
            </div>
          )}
        </aside>
      </div>
      </MaybeLoggedOutLockedSection>

      {!isOwner && creator && currentUser && (
        <>
          <TipJarSheet
            open={tipOpen}
            onClose={() => setTipOpen(false)}
            creator={creator}
            currentUserId={currentUser?.uid}
            userEmail={currentUser?.email || ""}
          />
          <WallNoteModal
            open={wallNoteOpen}
            onClose={() => setWallNoteOpen(false)}
            creatorId={creator.uid}
            creatorName={creator.displayName || creatorHandle}
          />
          <SubscribeModal
            isOpen={subscribeOpen}
            creatorId={creator.uid}
            creatorName={creator.displayName || creatorHandle}
            creatorTiers={tiers.length ? tiers : null}
            initialTierIndex={subscribeTierIndex}
            currentSubscriptionTier={userProfile?.subscriptionTiers?.[creator.uid] || null}
            profileThemeId={profileThemeId}
            profileThemeStyle={profileThemeStyle}
            onDismiss={() => {
              setSubscribeOpen(false);
              setSubscribeTierIndex(null);
            }}
            onSubscribe={() => {
              setSubscribeOpen(false);
              setSubscribeTierIndex(null);
            }}
          />
          <FollowingOverlay
            open={followingOpen}
            onClose={() => setFollowingOpen(false)}
            profileUser={creator}
            isOwner={isOwner}
            currentUser={userProfile || (currentUser ? { uid: currentUser.uid } : null)}
            onPrivacyChange={(value) => {
              setCreator((prev) => (prev ? { ...prev, followingListPrivate: value } : prev));
              if (isOwner && currentUser?.uid) refreshUserProfile?.(currentUser.uid);
            }}
            onOwnerUnfollow={(id) => {
              setCreator((prev) => {
                if (!prev) return prev;
                const next = followingIdsFromUser(prev).filter((x) => x !== id);
                return { ...prev, following: next };
              });
              if (isOwner && currentUser?.uid) refreshUserProfile?.(currentUser.uid);
            }}
          />
        </>
      )}
    </div>
  );
}
